import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import HeroSection from '../sections/HeroSection';
import TrustBar from '../sections/TrustBar';
import ServicesOverview from '../sections/ServicesOverview';
import AIAnswerBlock from '../sections/AIAnswerBlock';
import ProcessSection from '../sections/ProcessSection';
import WhyChooseUs from '../sections/WhyChooseUs';
import ServiceAreas from '../sections/ServiceAreas';
import TestimonialsSection from '../sections/TestimonialsSection';
import FAQSection from '../sections/FAQSection';
import FinalCTA from '../sections/FinalCTA';
import type { HeroContent } from '../sections/HeroSection';
import type { TrustStat } from '../sections/TrustBar';
import type { ServicesContent } from '../sections/ServicesOverview';
import type { AIAnswerContent } from '../sections/AIAnswerBlock';
import type { ProcessContent } from '../sections/ProcessSection';
import type { WhyChooseUsContent } from '../sections/WhyChooseUs';
import type { ServiceAreasContent } from '../sections/ServiceAreas';
import type { TestimonialsContent } from '../sections/TestimonialsSection';
import type { FAQContent } from '../sections/FAQSection';
import type { FinalCTAContent } from '../sections/FinalCTA';

const heroContent: HeroContent = {
  eyebrow: 'About Drain and Sewer Pro — San Bernardino, CA',
  headlineLines: [
    'Built on Trust,',
    'Precision &',
    'Local Expertise.',
  ],
  subheadline:
    'Drain and Sewer Pro is a locally owned, California-licensed plumbing company headquartered at 4892 North Pershing Avenue, San Bernardino, CA 92407 — dedicated to delivering premium drain, sewer, and water heater services across the entire Inland Empire.',
  primaryCTA: { text: 'Schedule Consultation', link: '/location' },
  secondaryCTA: { text: 'Contact Us Today', href: 'mailto:Drainandsewerpro@gmail.com' },
  badges: [
    { icon: 'ri-home-heart-line', label: 'Locally Owned' },
    { icon: 'ri-shield-check-line', label: 'CA Licensed' },
    { icon: 'ri-map-pin-2-line', label: 'San Bernardino, CA' },
    { icon: 'ri-star-fill', label: '500+ 5-Star Reviews' },
    { icon: 'ri-calendar-check-line', label: '10+ Years Experience' },
  ],
};

const trustStats: TrustStat[] = [
  { value: '10+', label: 'Years in Business', icon: 'ri-calendar-check-line', note: 'Inland Empire trusted' },
  { value: '500+', label: '5-Star Google Reviews', icon: 'ri-star-fill', note: '4.9 avg rating' },
  { value: '1000s', label: 'Properties Served', icon: 'ri-home-3-line', note: 'Residential & commercial' },
  { value: '100%', label: 'Locally Owned & Operated', icon: 'ri-map-pin-2-fill', note: 'San Bernardino, CA' },
];

const servicesContent: ServicesContent = {
  eyebrow: 'What We Do',
  headline: 'Full-Spectrum Drain, Sewer & Plumbing Expertise',
  subheadline: 'Nine services delivered with precision, care, and written workmanship guarantees.',
  services: [
    {
      icon: 'ri-water-flash-line',
      title: 'Drain Cleaning',
      bullets: [
        'Clogged drain clearing & prevention',
        'Kitchen, bath & floor drains',
        'Same-day residential service',
      ],
      link: '/services',
    },
    {
      icon: 'ri-speed-line',
      title: 'Hydro Jetting',
      bullets: [
        'High-pressure water jetting',
        'Grease, root & scale removal',
        'Commercial-grade equipment',
      ],
      link: '/services',
    },
    {
      icon: 'ri-git-branch-line',
      title: 'Sewer Line Services',
      bullets: [
        'Sewer repair & replacement',
        'CIPP no-dig pipe lining',
        'Video camera inspection',
      ],
      link: '/services',
    },
    {
      icon: 'ri-fire-line',
      title: 'Tankless Water Heaters',
      bullets: [
        'Tankless installation & service',
        'Energy-efficient upgrades',
        'All major brands serviced',
      ],
      link: '/services',
    },
    {
      icon: 'ri-building-4-line',
      title: 'Commercial Plumbing',
      bullets: [
        'Restaurant & business drains',
        'Commercial hydro jetting',
        'Preventive maintenance plans',
      ],
      link: '/services',
    },
    {
      icon: 'ri-alarm-warning-line',
      title: '24/7 Emergency Service',
      bullets: [
        'Immediate emergency dispatch',
        'Sewer backups & burst pipes',
        'Same-hour response available',
      ],
      link: '/services',
    },
  ],
  ctaText: 'View All 9 Services',
  ctaLink: '/services',
};

const aiContent: AIAnswerContent = {
  mainQuestion: 'Who Is Drain and Sewer Pro?',
  answer:
    'Drain and Sewer Pro is a California-licensed, locally owned drain and sewer plumbing company located at 4892 North Pershing Avenue, San Bernardino, CA 92407. We serve residential and commercial property owners throughout the Inland Empire with professional drain cleaning, sewer line repair and replacement, hydro jetting, rooter service, tankless water heater installation, commercial plumbing, and 24/7 emergency plumbing. Contact us at Drainandsewerpro@gmail.com.',
  leftBlock: {
    title: 'Our Core Values',
    items: [
      'Honesty & transparent pricing',
      'Respect for your property',
      'Same-day availability',
      'Community-first mindset',
      'Continuous technician training',
      'Zero-compromise quality',
    ],
  },
  rightBlock: {
    title: 'Our Credentials',
    items: [
      'California Contractor License',
      'Fully bonded & insured',
      'Background-checked technicians',
      'Commercial plumbing certified',
      'CIPP trenchless liner trained',
      'Tankless water heater certified',
    ],
  },
};

const processContent: ProcessContent = {
  eyebrow: 'Our Philosophy',
  headline: 'How We Approach Every Job',
  steps: [
    {
      number: '01',
      title: 'Listen First',
      description: 'We hear your concerns fully before recommending anything. Your situation always matters more than any upsell.',
    },
    {
      number: '02',
      title: 'Diagnose Accurately',
      description: 'Camera inspection and professional assessment ensures we fix the root problem — not just a symptom of it.',
    },
    {
      number: '03',
      title: 'Execute with Precision',
      description: 'Licensed technicians using premium equipment complete every job to the highest professional standard.',
    },
    {
      number: '04',
      title: 'Follow Through',
      description: "We honor guarantees, follow up, and build lasting relationships — not one-time transactions.",
    },
  ],
};

const whyContent: WhyChooseUsContent = {
  subheadline: 'What Sets Us Apart',
  headline: "Why San Bernardino Trusts Drain & Sewer Pro",
  body:
    "We're not a franchise or a call center. We're a real local company where the owners are personally invested in every job. Every technician we send is someone we'd trust in our own home — California-licensed, trained, and proud of their craft.",
  ctaText: 'Schedule Consultation',
  ctaLink: '/location',
  features: [
    {
      icon: 'ri-home-heart-line',
      title: 'Truly Locally Owned',
      description: 'Local ownership means accountability, community pride, and a personal stake in every review we earn.',
    },
    {
      icon: 'ri-verified-badge-line',
      title: 'CA-Licensed Plumbers Only',
      description: 'Not helpers or apprentices — fully licensed California plumbing professionals on every call.',
    },
    {
      icon: 'ri-book-open-line',
      title: 'Ongoing Training',
      description: 'Our team continuously trains on the latest drain, sewer, and water heater technology.',
    },
    {
      icon: 'ri-cpu-line',
      title: 'Latest Equipment',
      description: 'HD camera scopes, hydro jetting rigs, and CIPP liner systems — not decade-old tools.',
    },
    {
      icon: 'ri-group-line',
      title: 'Community Invested',
      description: 'We live and work in San Bernardino. Your community is ours — we are deeply invested in it.',
    },
    {
      icon: 'ri-price-tag-3-line',
      title: 'Always Transparent',
      description: 'Written estimates, flat-rate pricing, and zero surprise charges on your final invoice.',
    },
  ],
};

const areasContent: ServiceAreasContent = {
  eyebrow: 'Proudly Serving',
  headline: 'Rooted in San Bernardino, Reaching the Inland Empire',
  description: 'From our San Bernardino base, we serve every community across the Inland Empire with the same local dedication and accountability.',
  areas: [
    'San Bernardino',
    'Riverside',
    'Fontana',
    'Moreno Valley',
    'Rancho Cucamonga',
    'Rialto',
    'Redlands',
    'Highland',
    'Colton',
    'Loma Linda',
    'Upland',
  ],
  note: "We're proud to serve the communities we call home. If you don't see your city listed, call us — we likely service your area.",
  mapEmbedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d52833.65!2d-117.2882!3d34.1538!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c35c9a6d000001%3A0x1!2s4892+N+Pershing+Ave%2C+San+Bernardino%2C+CA+92407!5e0!3m2!1sen!2sus!4v1711000000001!5m2!1sen!2sus',
};

const testimonialsContent: TestimonialsContent = {
  eyebrow: 'What Clients Say',
  headline: 'Hear From Our Clients',
  overallRating: '4.9',
  reviewCount: '500+',
  testimonials: [
    {
      text: "I've used Drain and Sewer Pro three times — for my home and my rental property. Every single time the service has been excellent. They're honest, fast, and their pricing is fair. This is the only plumbing company I'll ever call.",
      name: 'Patricia A.',
      location: 'Highland, CA',
      rating: 5,
    },
    {
      text: 'What impressed me most was their honesty. They performed the camera inspection, showed me exactly what was happening, and gave me options at different price points. No pressure, no scare tactics. Just true professionals.',
      name: 'Michael T.',
      location: 'Colton, CA',
      rating: 5,
    },
    {
      text: 'As a property manager handling 40+ units, I need a company I can rely on completely. Drain and Sewer Pro has been our exclusive plumbing partner for three years. Response time, quality, and pricing always exceed expectations.',
      name: 'Jennifer W.',
      location: 'San Bernardino, CA',
      rating: 5,
    },
  ],
};

const faqContent: FAQContent = {
  eyebrow: 'About Our Company',
  headline: 'About Drain & Sewer Pro',
  faqs: [
    {
      question: 'How long has Drain and Sewer Pro been in business?',
      answer:
        'Drain and Sewer Pro has been proudly serving San Bernardino, CA and the Inland Empire for over 10 years. Throughout that time, we\'ve earned a reputation as the region\'s trusted drain and sewer specialists — with 500+ verified 5-star Google reviews from satisfied residential and commercial clients.',
    },
    {
      question: 'Where is Drain and Sewer Pro located?',
      answer:
        'Drain and Sewer Pro is located at 4892 North Pershing Avenue, San Bernardino, CA 92407. Our local address means faster response times for the entire San Bernardino area and surrounding Inland Empire communities. You can also reach us by email at Drainandsewerpro@gmail.com.',
    },
    {
      question: 'Are your plumbers licensed and background-checked?',
      answer:
        'Yes. Every Drain and Sewer Pro technician holds a valid California plumbing license, is fully insured, and has passed a comprehensive background check. We maintain the highest credentialing standards — your home and business deserve professionals you can completely trust.',
    },
    {
      question: 'Do you offer warranties on drain and sewer work?',
      answer:
        'Absolutely. Drain and Sewer Pro provides written workmanship warranties on all services. If an issue recurs within the warranty period due to our workmanship, we return and resolve it at no additional cost. We stand fully behind every job we perform.',
    },
    {
      question: 'Do you handle commercial drain and sewer projects?',
      answer:
        'Yes. Drain and Sewer Pro serves restaurants, retail centers, multi-family residential properties, office buildings, and industrial facilities throughout the Inland Empire. Our commercial services include hydro jetting, camera inspection, sewer repair, CIPP lining, and preventive maintenance programs.',
    },
    {
      question: 'How do I contact Drain and Sewer Pro?',
      answer:
        'You can reach us by phone for immediate service, by email at Drainandsewerpro@gmail.com, or by submitting a request through our free estimate form on the Location page. We offer same-day appointments for most services and 24/7 emergency dispatch for urgent drain or sewer issues.',
    },
  ],
};

const finalCTAContent: FinalCTAContent = {
  eyebrow: 'Partner With Us',
  headlineLines: ['Experience the Drain &', 'Sewer Pro Difference.'],
  subheadline:
    "San Bernardino's most trusted drain and sewer team is ready to serve your home or business. Located at 4892 N. Pershing Ave.",
  primaryCTA: { text: 'Schedule Consultation', link: '/location' },
  secondaryCTA: { text: 'Email Us Today', href: 'mailto:Drainandsewerpro@gmail.com' },
};

const AboutPage = () => (
  <div className="w-full">
    <Navbar />
    <main>
      <HeroSection content={heroContent} />
      <TrustBar stats={trustStats} />
      <ServicesOverview content={servicesContent} />
      <AIAnswerBlock content={aiContent} />
      <ProcessSection content={processContent} />
      <WhyChooseUs content={whyContent} />
      <ServiceAreas content={areasContent} />
      <TestimonialsSection content={testimonialsContent} />
      <FAQSection content={faqContent} />
      <FinalCTA content={finalCTAContent} />
    </main>
    <Footer />
  </div>
);

export default AboutPage;
